import { Formik } from "formik";
import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { toast, ToastBar, Toaster } from "react-hot-toast";
import * as yup from "yup";
import {
  createEMSBootAppV2,
  getTemplateForApplication,
  getUserProfile,
} from "../../../api";
import pricingValidation, {
  otherDefaultFeesPrettyNames,
} from "../../../components/modals/add-new-application-modal/pricing-validation";
import Breadcrumbs from "../../../v2/components/Breadcrumbs";
import Alert from "../../../v2/components/alert";
import useUserStore from "../../../v2/stores/user";
import SkeletonLoader from "../../components/skeleton-loader";
import ButtonIcon from "../../components/ButtonIcon/ButtonIcon";
import Select from "../../components/form/select";
import ContactInformation from "./components/contact-info";
import AdditionalAppInfo from "./components/additional-app-info";
import Cost from "./components/cost";
import Details from "./components/details";
import Equipments from "./components/equipments";
import SendLinkMerchantModal from "./components/merchant-link-modal";
import Pricing from "./components/pricing";
import Processors from "./components/processor";
import Footer from "./components/footer";
import {
  contactInfoSchema,
  detailsInfoSchema,
  equipmentSchema,
  createPricingInfoSchema,
  processorInfoSchema,
  additionalAppInfoSchema,
} from "./validation-schema";
import useOrganizationStore, {
  fetchPricingDefaults,
} from "../../stores/organization";
import useSalesFormStore from "../../stores/sales-form";
import { useEMSValidation } from "../../hooks/useEMSValidation";
import { scrollToError } from "./utils/scroll-to-error";
import styles from "./style.module.scss";

const createFormSchema = (pricingDefaults, isKurvPayEntity) =>
  yup.object({
    ...contactInfoSchema.fields,
    ...createPricingInfoSchema(pricingDefaults).fields,
    ...equipmentSchema,
    ...detailsInfoSchema.fields,
    ...processorInfoSchema.fields,
    ...(isKurvPayEntity ? additionalAppInfoSchema.fields : {}),
  });

// let dual_pricing_validation = pricingValidation.pricingValidation.dual_pricing;
const surcharge_validation = pricingValidation.pricingValidation.surcharge;
const flat_rate_validation = pricingValidation.pricingValidation.flat_rate;
const ic_plus_validation = pricingValidation.pricingValidation.ic_plus;
const tiered_validation = pricingValidation.pricingValidation.tiered;
const ach_validation = pricingValidation.pricingValidation.ach;

const contactInfoField = [
  "first_name",
  "last_name",
  "dba_name",
  "country",
  "dba_street",
  "dba_city",
  "dba_state",
  "dba_zip",
  "postal_code",
  "email",
  "mobile",
  "is_merchant_referral",
  "referrer_name",
  "referrer_mid",
  "is_tm",
  "telemarketer_name",
  "cold_call",
  "tm_lead",
];

const pricingInfoFields = [
  "pricing_model",
  "dual_pricing_rate",
  "dual_pricing_per_transaction",
  "dual_pricing_monthly_fee",
  "surcharge_rate",
  "surcharge_per_transaction",
  "surcharge_monthly_fee",
  "flat_rate",
  "flat_rate_transaction",
  "flat_rate_amex",
  "flat_rate_amex_transaction",
  "ic_plus_rate",
  "ic_plus_transaction_fee",
  "ic_plus_amex_rate",
  "ic_plus_amex_transaction_fee",
  "tiered_qual_rate",
  "tiered_qual_transaction",
  "tiered_qual_debit_rate",
  "tiered_qual_debit_per_transaction",
  "tiered_mid_qual_rate",
  "tiered_mid_qual_transaction",
  "tiered_mid_qual_debit_rate",
  "tiered_mid_qual_debit_per_transaction",
  "tiered_non_qual_rate",
  "tiered_non_qual_transaction",
  "tiered_non_qual_debit_rate",
  "tiered_non_qual_debit_per_transaction",
  "tiered_amex_structure",
  "amex_tier_one_rate",
  "amex_tier_one_fee",
  "amex_tier_two_rate",
  "amex_tier_two_fee",
  "amex_tier_three_rate",
  "amex_tier_three_fee",
  "amex_tier_four_rate",
  "amex_tier_four_fee",
  "tiered_amex_flat_rate",
  "tiered_amex_flat_transaction",
  "tiered_amex_ic_plus_rate",
  "tiered_amex_ic_plus_transaction",
  "ebt",
  "fnf_number",
  "ebt_transactions",
  "var_sheet_request",
  // "reprogram_hardware",
  // "use_existing_gateway",
  // "existing_gateway",
  "var_only",
];

const detailsInfoField = [
  "who_to_deploy_equipment",
  "who_to_pay_for_equipment",
  "who_to_deploy_gateway",
  "billing_sequence",
  "cover_cancellation",
  "reimbursement_maximum",
  "expedited_funding",
  "batch_time",
  "equipment_address",
  "invoice_address",
  // "special_instructions",
];

export const reprogrammedHardwareInitialValues = {
  equipment_type: "",
  equipment_model: "",
  serial_number: "",
  quantity: "1",
  application_type: "",
  comm_method: "ip",
  auto_batch: "no",
  auto_batch_time: "",
  special_instructions: "",
};

const CreateAppPage = ({ history, match }) => {
  const user = useUserStore((state) => state.user);
  const organizationId = useUserStore((state) => state.organizationId);
  const agentUrl = useUserStore((state) => state.agentUrl);
  const userLoading = useUserStore((state) => state.userLoading);
  const isPCUser = useUserStore((state) => state.isPCUser);
  const isEMSUser = useUserStore((state) => state.isEMSUser);
  const isKurvPayEntity = useUserStore((state) => state.isKurvPayEntity);
  const pricingDefaults = useOrganizationStore(
    (state) => state.pricingDefaults,
  );
  const pricingDefaultsLoading = useOrganizationStore(
    (state) => state.pricingDefaultsLoading,
  );
  const [continueURL, setContinueURL] = useState();
  const [submissionType, setSubmissionType] = useState("");
  const [sendLinkModal, setSendLinkModal] = useState(false);
  const [openSections, setOpenSections] = useState({
    contact: true,
    pricing: false,
    equipments: false,
    details: false,
    processors: false,
    additionalAppInfo: true,
  });
  const [templateList, setTemplateList] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templateListLoading, setTemplateListLoading] = useState(true);
  const { validatePhone } = useEMSValidation();
  const [emsOfficeOptions, setEMSOfficeOptions] = useState([]);

  useEffect(() => {
    if (organizationId) {
      fetchPricingDefaults(organizationId);
    }
  }, [organizationId, fetchPricingDefaults]);

  // Create a function to get initial values instead of using state
  const getInitialValues = (pricingDefaults = {}) => ({
    first_name: "",
    last_name: "",
    dba_name: "",
    dba_street: "",
    dba_city: "",
    dba_state: "",
    dba_zip: "",
    postal_code: "",
    email: "",
    mobile: "",
    // NOTE: This is a hack/weird way to set a country; which is doubled up
    // as a form language field on the backend.
    // Set the country to lowercase 'ca' if it's Canada
    // Set the country to lowercase 'en' if it's USA
    country: match?.params?.country === "ca" ? "ca" : "en",

    // pricing fields
    pricing_model: "",
    pricing_approved_by_rate_desk: "no",
    // Dual pricing fields
    dual_pricing_rate:
      pricingDefaults?.dual_pricing?.dual_pricing_rate?.default ?? "",
    dual_pricing_per_transaction:
      pricingDefaults?.dual_pricing?.dual_pricing_per_transaction?.default ??
      "",
    dual_pricing_monthly_fee:
      pricingDefaults?.dual_pricing?.dual_pricing_monthly_fee?.default ?? "",
    dual_pricing_pin_debit_network_fee: "no",
    dual_pricing_pin_debit_rate:
      pricingDefaults?.dual_pricing?.dual_pricing_pin_debit_rate?.default ?? "",
    dual_pricing_pin_debit_per_transaction:
      pricingDefaults?.dual_pricing?.dual_pricing_pin_debit_per_transaction
        ?.default ?? "",

    // surcharge
    surcharge_rate: pricingDefaults?.surcharge?.surcharge_rate?.default ?? "",
    surcharge_per_transaction:
      pricingDefaults?.surcharge?.surcharge_per_transaction?.default ?? "",
    surcharge_monthly_fee:
      pricingDefaults?.surcharge?.surcharge_monthly_fee?.default ?? "",
    surcharge_pin_debit_network_fee: "yes",
    surcharge_pin_debit_rate:
      pricingDefaults?.surcharge?.surcharge_pin_debit_rate?.default ?? "",
    surcharge_pin_debit_per_transaction:
      pricingDefaults?.surcharge?.surcharge_pin_debit_per_transaction
        ?.default ?? "",
    surcharge_qual_debit_rate:
      pricingDefaults?.surcharge?.surcharge_qual_debit_rate?.default ?? "",
    surcharge_qual_debit_per_transaction:
      pricingDefaults?.surcharge?.surcharge_qual_debit_per_transaction
        ?.default ?? "",
    surcharge_mid_qual_debit_rate:
      pricingDefaults?.surcharge?.surcharge_mid_qual_debit_rate?.default ?? "",
    surcharge_mid_qual_debit_per_transaction:
      pricingDefaults?.surcharge?.surcharge_mid_qual_debit_per_transaction
        ?.default ?? "",
    surcharge_non_qual_debit_rate:
      pricingDefaults?.surcharge?.surcharge_non_qual_debit_rate?.default ?? "",
    surcharge_non_qual_debit_per_transaction:
      pricingDefaults?.surcharge?.surcharge_non_qual_debit_per_transaction
        ?.default,
    surcharge_signature_debit_rate:
      pricingDefaults?.surcharge?.surcharge_signature_debit_rate?.default ?? "",
    surcharge_signature_debit_per_transaction:
      pricingDefaults?.surcharge?.surcharge_signature_debit_per_transaction
        ?.default ?? "",

    // Flat Rate
    flat_rate: pricingDefaults?.flat_rate?.flat_rate?.default ?? "",
    flat_rate_amex_toggle: "yes",
    flat_rate_transaction:
      pricingDefaults?.flat_rate?.flat_rate_transaction?.default ?? "",
    flat_rate_amex: pricingDefaults?.flat_rate?.flat_rate_amex?.default ?? "",
    flat_rate_amex_transaction:
      pricingDefaults?.flat_rate?.flat_rate_amex_transaction?.default ?? "",
    flat_rate_pin_debit_network_fee: "yes",
    flat_rate_pin_debit_rate:
      pricingDefaults?.flat_rate?.flat_rate_pin_debit_rate?.default ?? "",
    flat_rate_pin_debit_per_transaction:
      pricingDefaults?.flat_rate?.flat_rate_pin_debit_per_transaction
        ?.default ?? "",

    // IC+
    ic_plus_rate: pricingDefaults?.ic_plus?.ic_plus_rate?.default ?? "",
    ic_plus_amex_toggle: "yes",
    ic_plus_transaction_fee:
      pricingDefaults?.ic_plus?.ic_plus_transaction_fee?.default ?? "",
    ic_plus_amex_rate:
      pricingDefaults?.ic_plus?.ic_plus_amex_rate?.default ?? "",
    ic_plus_amex_transaction_fee:
      pricingDefaults?.ic_plus?.ic_plus_amex_transaction_fee?.default ?? "",
    ic_plus_pin_debit_network_fee: "yes",
    ic_plus_pin_debit_rate:
      pricingDefaults?.ic_plus?.ic_plus_pin_debit_rate?.default ?? "",
    ic_plus_pin_debit_per_transaction:
      pricingDefaults?.ic_plus?.ic_plus_pin_debit_per_transaction?.default ??
      "",
    // tiered
    tiered_qual_rate: pricingDefaults?.tiered?.tiered_qual_rate?.default ?? "",
    tiered_qual_transaction:
      pricingDefaults?.tiered?.tiered_qual_transaction?.default ?? "",
    tiered_qual_debit_rate:
      pricingDefaults?.tiered?.tiered_qual_debit_rate?.default ?? "",
    tiered_qual_debit_per_transaction:
      pricingDefaults?.tiered?.tiered_qual_debit_per_transaction?.default ?? "",
    tiered_mid_qual_rate:
      pricingDefaults?.tiered?.tiered_mid_qual_rate?.default ?? "",
    tiered_mid_qual_transaction:
      pricingDefaults?.tiered?.tiered_mid_qual_transaction?.default ?? "",
    tiered_mid_qual_debit_rate:
      pricingDefaults?.tiered?.tiered_mid_qual_debit_rate?.default ?? "",
    tiered_mid_qual_debit_per_transaction:
      pricingDefaults?.tiered?.tiered_mid_qual_debit_per_transaction?.default ??
      "",
    tiered_non_qual_rate:
      pricingDefaults?.tiered?.tiered_non_qual_rate?.default ?? "",
    tiered_non_qual_transaction:
      pricingDefaults?.tiered?.tiered_non_qual_transaction?.default ?? "",
    tiered_non_qual_debit_rate:
      pricingDefaults?.tiered?.tiered_non_qual_debit_rate?.default ?? "",
    tiered_non_qual_debit_per_transaction:
      pricingDefaults?.tiered?.tiered_non_qual_debit_per_transaction?.default ??
      "",
    tiered_pin_debit_network_fee: "yes",
    tiered_pin_debit_rate:
      pricingDefaults?.tiered?.tiered_pin_debit_rate?.default ?? "",
    tiered_pin_debit_per_transaction:
      pricingDefaults?.tiered?.tiered_pin_debit_per_transaction?.default ?? "",

    tiered_amex_structure: "tiered", // tiered, flat_rate, ic_plus
    tiered_amex_toggle: "no",

    amex_tier_one_rate:
      pricingDefaults?.tiered?.amex_tier_one_rate?.default ?? "",
    amex_tier_one_fee:
      pricingDefaults?.tiered?.amex_tier_one_fee?.default ?? "",
    amex_tier_two_rate:
      pricingDefaults?.tiered?.amex_tier_two_rate?.default ?? "",
    amex_tier_two_fee:
      pricingDefaults?.tiered?.amex_tier_two_fee?.default ?? "",
    amex_tier_three_rate:
      pricingDefaults?.tiered?.amex_tier_three_rate?.default ?? "",
    amex_tier_three_fee:
      pricingDefaults?.tiered?.amex_tier_three_fee?.default ?? "",
    amex_tier_four_rate:
      pricingDefaults?.tiered?.amex_tier_four_rate?.default ?? "",
    amex_tier_four_fee:
      pricingDefaults?.tiered?.amex_tier_four_fee?.default ?? "",
    tiered_type: "swiped",

    tiered_amex_flat_rate:
      pricingDefaults?.tiered?.tiered_amex_flat_rate?.default ?? "",
    tiered_amex_flat_transaction:
      pricingDefaults?.tiered?.tiered_amex_flat_transaction?.default ?? "",

    tiered_amex_ic_plus_rate:
      pricingDefaults?.tiered?.tiered_amex_ic_plus_rate?.default ?? "",
    tiered_amex_ic_plus_transaction:
      pricingDefaults?.tiered?.tiered_amex_ic_plus_transaction?.default ?? "",
    //ach
    ach_discount_rate: pricingDefaults?.ach?.ach_discount_rate?.default ?? "",
    ach_transaction_fee_per_item:
      pricingDefaults?.ach?.ach_transaction_fee_per_item?.default ?? "",

    ach_statement_fee: pricingDefaults?.ach?.ach_statement_fee?.default ?? "",
    ach_monthly_minimum:
      pricingDefaults?.ach?.ach_monthly_minimum?.default ?? "",
    ach_return_fee: pricingDefaults?.ach?.ach_return_fee?.default ?? "",
    ach_noc_fee: pricingDefaults?.ach?.ach_noc_fee?.default ?? "",
    ach_unauthorized_return_fee:
      pricingDefaults?.ach?.ach_unauthorized_return_fee?.default ?? "",
    ach_same_day_fee: pricingDefaults?.ach?.ach_same_day_fee?.default ?? "",
    ach_bank_validation_fee:
      pricingDefaults?.ach?.ach_bank_validation_fee?.default ?? "",
    ach_by_web: "no",
    ach_by_phone: "no",
    ach_by_preauth: "no",
    ach_credit_transaction: "no",
    add_ach: "no",

    // kurv pricing
    kurv_pricing_swiped_rate:
      pricingDefaults?.kurv_pricing?.kurv_pricing_swiped_rate?.default ?? "",
    kurv_pricing_swiped_txn_fee:
      pricingDefaults?.kurv_pricing?.kurv_pricing_swiped_txn_fee?.default ?? "",
    kurv_pricing_keyed_rate:
      pricingDefaults?.kurv_pricing?.kurv_pricing_keyed_rate?.default ?? "",
    kurv_pricing_keyed_txn_fee:
      pricingDefaults?.kurv_pricing?.kurv_pricing_keyed_txn_fee?.default ?? "",
    kurv_pricing_amex_toggle: "yes",
    kurv_pricing_amex_rate:
      pricingDefaults?.kurv_pricing?.kurv_pricing_amex_rate?.default ?? "",
    kurv_pricing_amex_txn_fee:
      pricingDefaults?.kurv_pricing?.kurv_pricing_amex_txn_fee?.default ?? "",
    kurv_pricing_pin_debit_network_fee: "yes",
    kurv_pricing_pin_debit_rate:
      pricingDefaults?.kurv_pricing?.kurv_pricing_pin_debit_rate?.default ?? "",
    kurv_pricing_pin_debit_per_transaction:
      pricingDefaults?.kurv_pricing?.kurv_pricing_pin_debit_per_transaction
        ?.default ?? "",

    //Note: Below field is not used anywhere, but kept so as not to break anything.
    monthly_fee: "49.00",

    // ebt
    ebt: "no",
    fnf_number: "",

    // other default fees
    ...Object.keys(otherDefaultFeesPrettyNames).reduce((acc, key) => {
      acc[key] =
        pricingDefaults?.dual_pricing?.[key]?.default ??
        pricingDefaults?.default_fees?.[key]?.default;
      return acc;
    }, {}),

    // Merchant referral fields
    is_merchant_referral: "no",
    referrer_name: "",
    referrer_mid: "",
    is_tm: "no",
    telemarketer_name: "",
    cold_call: "no",
    tm_lead: "no",

    var_sheet_request: false,
    var_only: {},

    equipments: [],
    other_gateway: "",

    // details
    who_to_deploy_equipment: "",
    who_to_pay_for_equipment: "",
    who_to_deploy_gateway: "",
    billing_sequence: "daily",
    cover_cancellation: "no",
    reimbursement_maximum: "",
    expedited_funding: "no",
    batch_time: "06:00 PM",
    equipment_address: "dba_address",
    invoice_address: "agent_address",
    special_instructions: "",
    uw_notes: "",
    is_guarantor: true,

    // processor selection
    processors: [],
    payliance_industry: "",

    // Legal Business Address
    legal_address_same_as_dba: false,
    legal_address: "",
    legal_city: "",
    legal_state: "",
    legal_zip: "",

    // Template tracking
    templateApplied: false,
    nonEditableTemplateApplied: false,
    processorSelectionLocked: false,
    template_id: null,

    // additional fields
    mcc_id: null,
    ems_office_id: "",
    isPCUser,
  });

  const [initialValuesBackup, setInitialValuesBackup] = useState(
    getInitialValues(pricingDefaults),
  );

  const onSubmit = (values, { setTouched, setSubmitting, setErrors }) => {
    //Note: Touching all fields so that the errors are shown
    setTouched({});

    const submitValues = { ...values };
    submitValues.submission_type =
      submissionType === "continue" ? "create_app" : submissionType;

    createEMSBootAppV2(submitValues)
      .then((res) => {
        toast.loading("Creating application...");

        if (!res?.status) {
          toast.dismiss();
          toast.error("Failed to create application");
          console.error("Failed to create application.");
          return;
        }

        if (!res?.data?.editslug) {
          toast.dismiss();
          toast.error("Failed to create application");
          return;
        }

        toast.dismiss();
        toast.success("Application created successfully");

        const continueURLNonPrefixed = `app/${agentUrl}/${res.data.editslug}`;
        setContinueURL(continueURLNonPrefixed);

        if (submissionType === "continue") {
          history.push(`/${continueURLNonPrefixed}`);
        }

        if (submissionType === "send_to_merchant") {
          setSendLinkModal(true);
        }

        if (submissionType === "create_app") {
          history.push("/applications");
        }

        if (submissionType === "send_app_for_signature") {
          history.push("/applications");
        }
      })
      .catch((error) => {
        console.error(error);

        if (error.status !== 422) {
          toast.error("Failed to submit application");
          return;
        }

        if (!error?.data?.errors) {
          toast.error("Failed to submit application");
          return;
        }

        const formattedErrors = {};
        Object.keys(error.data.errors).forEach((key) => {
          if (
            Array.isArray(error.data.errors[key]) &&
            error.data.errors[key].length > 0
          ) {
            formattedErrors[key] = error.data.errors[key][0];
          }
        });

        setErrors(formattedErrors);
        setTouched(
          Object.keys(formattedErrors).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {}),
        );

        setOpenSections({
          contact: true,
          pricing: true,
          equipments: true,
          details: true,
          processors: true,
        });
        scrollToError(formattedErrors);

        const firstErrorKey = Object.keys(formattedErrors)[0];
        if (firstErrorKey) {
          toast.error(
            `Failed to submit application: ${formattedErrors[firstErrorKey]}`,
          );
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const fetchEMSOffices = async () => {
    try {
      const userRes = await getUserProfile();
      if (userRes.success) {
        setEMSOfficeOptions(userRes.data.ems_office);
      }
    } catch (error) {
      console.error("Failed to fetch EMS offices:", error);
    }
  };

  useEffect(() => {
    fetchEMSOffices();
    getTemplateForApplication().then((res) => {
      if (res.status) {
        setTemplateListLoading(false);
        setTemplateList(res.data);
        setTemplateOptions(
          res.data.map((item) => ({
            label: item.name,
            value: item.id,
          })),
        );
      }
    });
  }, []);

  const isV1Template = (template) => {
    return !Array.isArray(template?.data?.equipments);
  };

  const applyTemplate = (value, setValues, currentValues = {}) => {
    const template = templateList.find((item) => item.id === value);

    if (!template) {
      return;
    }

    const equipments = isV1Template(template)
      ? Object.entries(template?.data?.equipments).flatMap(
          ([equipmentType, equipmentGroups]) => {
            return Object.entries(equipmentGroups).flatMap(
              ([_, equipmentList]) => {
                return Array.isArray(equipmentList)
                  ? equipmentList.map((equipment) => ({
                      ...equipment,
                      type: equipmentType,
                    }))
                  : [];
              },
            );
          },
        )
      : template?.data?.equipments;

    const baseValues =
      Object.keys(currentValues).length > 0
        ? currentValues
        : getInitialValues();
    const templateHasProcessors =
      Array.isArray(template?.data?.processors) &&
      template.data.processors.length > 0;

    const contactInfoPreserved = {};
    contactInfoField.forEach((field) => {
      if (
        currentValues[field] !== undefined &&
        currentValues[field] !== null &&
        currentValues[field] !== ""
      ) {
        contactInfoPreserved[field] = currentValues[field];
      }
    });

    setValues({
      ...baseValues,
      ebt: template?.data?.ebt || "no",
      is_tm: template?.data?.is_tm || "yes",
      avs_fee: template?.data?.avs_fee || "0.10",
      country: template?.data?.country || "en",
      etf_fee: template?.data?.etf_fee || "595.00",
      tm_lead: template?.data?.tm_lead || "no",
      var_only: template?.data?.var_only || null,
      batch_fee: template?.data?.batch_fee || "0.30",
      cold_call: template?.data?.cold_call || "no",
      flat_rate: template?.data?.flat_rate || "3.00",
      setup_fee: template?.data?.setup_fee || "125.00",
      voice_aru: template?.data?.voice_aru || "0.60",
      batch_time: template?.data?.batch_time || "06:00 PM",
      fnf_number: template?.data?.fnf_number || null,
      data_breach: template?.data?.data_breach || "15.00",
      monthly_fee: template?.data?.monthly_fee || "49.00",
      postal_code: template?.data?.postal_code || null,
      selectedOrg: template?.data?.selectedOrg || null,
      ic_plus_rate: template?.data?.ic_plus_rate || "0.15",
      referrer_mid: template?.data?.referrer_mid || "",
      selectedUser: template?.data?.selectedUser || null,
      other_gateway: template?.data?.other_gateway || null,
      tiered_amex_structure: template?.data?.pricing_model || "tiered",
      tiered_amex_toggle: template?.data?.tiered_amex_toggle || "no",
      pricing_model: template?.data?.qualifiedtype || "dual_pricing",
      referrer_name: template?.data?.referrer_name || "",
      chargeback_fee: template?.data?.chargeback_fee || "45.00",
      flat_rate_amex: template?.data?.flat_rate_amex || "3.00",
      flat_rate_amex_toggle: template?.data?.flat_rate_amex_toggle || "no",
      surcharge_rate: template?.data?.surcharge_rate || "3.00",
      invoice_address: template?.data?.invoice_address || "agent_address",
      monthly_minimum: template?.data?.monthly_minimum || "25.00",
      pre_dispute_fee: template?.data?.pre_dispute_fee || "45.00",
      card_brand_fee:
        template?.data?.card_brand_fee ??
        pricingDefaults?.[template?.data?.qualifiedtype]?.card_brand_fee?.default ??
        "0.00",
      pci_non_compliance_fee:
        template?.data?.pci_non_compliance_fee ??
        pricingDefaults?.[template?.data?.qualifiedtype]?.pci_non_compliance_fee?.default ??
        "0.00",
      retrieval_fee:
        template?.data?.retrieval_fee ??
        pricingDefaults?.[template?.data?.qualifiedtype]?.retrieval_fee?.default ??
        "25.00",
      auth_transaction: template?.data?.auth_transaction || "0.25",
      billing_sequence: template?.data?.billing_sequence || "daily",
      ebt_transactions: template?.data?.ebt_transactions || 0,
      monthly_cellular: template?.data?.monthly_cellular || "25.00",
      tiered_qual_rate: template?.data?.tiered_qual_rate || "1.75",
      amex_tier_one_fee: template?.data?.amex_tier_one_fee || "0.20",
      amex_tier_two_fee: template?.data?.amex_tier_two_fee ?? "",
      dual_pricing_rate: template?.data?.dual_pricing_rate || 4,
      equipment_address: template?.data?.equipment_address || "dba_address",
      expedited_funding: template?.data?.expedited_funding || "yes",
      ic_plus_amex_rate: template?.data?.ic_plus_amex_rate || "0.15",
      ic_plus_amex_toggle: template?.data?.ic_plus_amex_toggle || "no",
      representment_fee: template?.data?.representment_fee || "45.00",
      returned_ach_item: template?.data?.returned_ach_item || "25.00",
      selected_template: template?.data?.selected_template || null,
      telemarketer_name: template?.data?.telemarketer_name || "",
      var_sheet_request: template?.data?.var_sheet_request || "yes",
      amex_tier_four_fee: template?.data?.amex_tier_four_fee ?? "",
      amex_tier_one_rate: template?.data?.amex_tier_one_rate || "3.50",
      amex_tier_two_rate: template?.data?.amex_tier_two_rate ?? "",
      cover_cancellation: template?.data?.cover_cancellation || "no",
      monthly_access_fee: template?.data?.monthly_access_fee || "15.00",
      amex_tier_four_rate: template?.data?.amex_tier_four_rate ?? "",
      amex_tier_three_fee: template?.data?.amex_tier_three_fee ?? "",
      reprogram_equipment: template?.data?.reprogram_equipment || true,
      amex_tier_three_rate: template?.data?.amex_tier_three_rate ?? "",
      cellular_transaction: template?.data?.cellular_transaction || "0.10",
      is_merchant_referral: template?.data?.is_merchant_referral || "no",
      semi_annual_tech_fee: template?.data?.semi_annual_tech_fee || "75.00",
      special_instructions: template?.data?.special_instructions || "",
      tiered_mid_qual_rate: template?.data?.tiered_mid_qual_rate ?? "",
      tiered_non_qual_rate: template?.data?.tiered_non_qual_rate ?? "",
      flat_rate_transaction: template?.data?.flat_rate_transaction || "0.15",
      surcharge_monthly_fee: template?.data?.surcharge_monthly_fee || "49.00",
      tiered_amex_flat_rate: template?.data?.tiered_amex_flat_rate || "3.50",
      tiered_pin_debit_rate: template?.data?.tiered_pin_debit_rate || "0.00",
      who_to_deploy_gateway: template?.data?.who_to_deploy_gateway || "agent",
      ic_plus_pin_debit_rate: template?.data?.ic_plus_pin_debit_rate || "0.00",
      tiered_qual_debit_rate: template?.data?.tiered_qual_debit_rate || "1.50",
      ic_plus_transaction_fee:
        template?.data?.ic_plus_transaction_fee || "0.15",
      tiered_qual_transaction:
        template?.data?.tiered_qual_transaction || "0.15",
      who_to_deploy_equipment:
        template?.data?.who_to_deploy_equipment || "agent",
      dual_pricing_monthly_fee:
        template?.data?.dual_pricing_monthly_fee || "49.00",
      flat_rate_pin_debit_rate:
        template?.data?.flat_rate_pin_debit_rate || "2.00",
      reprogram_equipment_name:
        template?.data?.reprogram_equipment_name || null,
      surcharge_pin_debit_rate:
        template?.data?.surcharge_pin_debit_rate || "0.00",
      tiered_amex_ic_plus_rate:
        template?.data?.tiered_amex_ic_plus_rate || "3.50",
      who_to_pay_for_equipment:
        template?.data?.who_to_pay_for_equipment || "agent",
      surcharge_per_transaction:
        template?.data?.surcharge_per_transaction || "0.15",
      surcharge_qual_debit_rate:
        template?.data?.surcharge_qual_debit_rate || "1.50",
      flat_rate_amex_transaction:
        template?.data?.flat_rate_amex_transaction || "0.15",
      tiered_mid_qual_debit_rate:
        template?.data?.tiered_mid_qual_debit_rate ?? "",
      tiered_non_qual_debit_rate:
        template?.data?.tiered_non_qual_debit_rate ?? "",
      dual_pricing_pin_debit_rate:
        template?.data?.dual_pricing_pin_debit_rate || "0.00",
      tiered_mid_qual_transaction:
        template?.data?.tiered_mid_qual_transaction ?? "",
      tiered_non_qual_transaction:
        template?.data?.tiered_non_qual_transaction ?? "",
      ic_plus_amex_transaction_fee:
        template?.data?.ic_plus_amex_transaction_fee || "0.15",
      tiered_amex_flat_transaction:
        template?.data?.tiered_amex_flat_transaction || "0.20",
      tiered_pin_debit_network_fee:
        template?.data?.tiered_pin_debit_network_fee || "yes",
      ic_plus_pin_debit_network_fee:
        template?.data?.ic_plus_pin_debit_network_fee || "yes",
      pricing_approved_by_rate_desk:
        template?.data?.pricing_approved_by_rate_desk || "no",
      surcharge_mid_qual_debit_rate:
        template?.data?.surcharge_mid_qual_debit_rate || "1.99",
      surcharge_non_qual_debit_rate:
        template?.data?.surcharge_non_qual_debit_rate || "2.49",
      surcharge_signature_debit_rate:
        template?.data?.surcharge_signature_debit_rate || "3.00",
      flat_rate_pin_debit_network_fee:
        template?.data?.flat_rate_pin_debit_network_fee || "yes",
      surcharge_pin_debit_network_fee:
        template?.data?.surcharge_pin_debit_network_fee || "yes",
      tiered_amex_ic_plus_transaction:
        template?.data?.tiered_amex_ic_plus_transaction || "0.20",
      tiered_pin_debit_per_transaction:
        template?.data?.tiered_pin_debit_per_transaction || "0.00",
      ic_plus_pin_debit_per_transaction:
        template?.data?.ic_plus_pin_debit_per_transaction || "0.00",
      tiered_qual_debit_per_transaction:
        template?.data?.tiered_qual_debit_per_transaction || "0.15",
      dual_pricing_pin_debit_network_fee:
        template?.data?.dual_pricing_pin_debit_network_fee || "yes",
      flat_rate_pin_debit_per_transaction:
        template?.data?.flat_rate_pin_debit_per_transaction || "0.15",
      surcharge_pin_debit_per_transaction:
        template?.data?.surcharge_pin_debit_per_transaction || "0.00",
      surcharge_qual_debit_per_transaction:
        template?.data?.surcharge_qual_debit_per_transaction || "0.15",
      tiered_mid_qual_debit_per_transaction:
        template?.data?.tiered_mid_qual_debit_per_transaction ?? "",
      tiered_non_qual_debit_per_transaction:
        template?.data?.tiered_non_qual_debit_per_transaction ?? "",
      dual_pricing_pin_debit_per_transaction:
        template?.data?.dual_pricing_pin_debit_per_transaction || "0.00",
      surcharge_mid_qual_debit_per_transaction:
        template?.data?.surcharge_mid_qual_debit_per_transaction || "0.15",
      surcharge_non_qual_debit_per_transaction:
        template?.data?.surcharge_non_qual_debit_per_transaction || "0.15",
      surcharge_signature_debit_per_transaction:
        template?.data?.surcharge_signature_debit_per_transaction || "0.15",
      equipments,
      processors: templateHasProcessors
        ? template.data.processors
        : baseValues.processors || [],
      //ACH
      ach_by_web: template?.data?.ach_by_web || "no",
      ach_by_phone: template?.data?.ach_by_phone || "no",
      ach_by_preauth: template?.data?.ach_by_preauth || "no",
      ach_credit_transaction: template?.data?.ach_credit_transaction || "no",
      ach_discount_rate: template?.data?.ach_discount_rate || "",
      ach_transaction_fee_per_item:
        template?.data?.ach_transaction_fee_per_item ||
        ach_validation.ach_transaction_fee_per_item.default,
      ach_statement_fee:
        template?.data?.ach_statement_fee ||
        ach_validation.ach_statement_fee.default,
      ach_monthly_minimum:
        template?.data?.ach_monthly_minimum ||
        ach_validation.ach_monthly_minimum.default,
      ach_return_fee:
        template?.data?.ach_return_fee || ach_validation.ach_return_fee.default,
      ach_noc_fee:
        template?.data?.ach_noc_fee || ach_validation.ach_noc_fee.default,
      ach_unauthorized_return_fee:
        template?.data?.ach_unauthorized_return_fee ||
        ach_validation.ach_unauthorized_return_fee.default,
      ach_same_day_fee: template?.data?.ach_same_day_fee || "",
      ach_bank_validation_fee: template?.data?.ach_bank_validation_fee || "",
      add_ach: template?.data?.add_ach || "no",

      // Set template applied flag
      templateApplied: true,
      nonEditableTemplateApplied: !template?.is_editable,
      processorSelectionLocked:
        !template?.is_editable && templateHasProcessors,
      template_id: value || null,

      //details section
      uw_notes: template?.data?.uw_notes || "",
      reimbursement_maximum: template?.data?.reimbursement_maximum || "",

      // additional fields
      mcc_id: template?.data?.mcc_id?.value || null,
      ems_office_id: template?.data?.ems_office_id?.label || null,

      // Preserve user's contact information
      ...contactInfoPreserved,
    });

    setOpenSections((prev) => ({
      ...prev,
      pricing: true,
      equipments: true,
    }));

    toast.success(`Template "${template.name}" applied successfully!`);
  };

  const dataLoading = pricingDefaultsLoading || templateListLoading;

  return (
    <div className={styles.page}>
      <Breadcrumbs
        items={[
          { label: "Application", to: "/applications" },
          { label: "Create application" },
        ]}
      />

      <h1 className={styles.title}>Create app</h1>
      <Formik
        key={match?.params?.country}
        enableReinitialize
        validationSchema={createFormSchema(pricingDefaults, isKurvPayEntity)}
        onSubmit={onSubmit}
        initialValues={getInitialValues(pricingDefaults)}
        validateOnBlur={true}
        validateOnChange={true}
        validationContext={{ organizationId }}
      >
        {({
          handleSubmit,
          values,
          errors,
          touched,
          isSubmitting,
          setFieldValue,
          setFieldError,
          setFieldTouched,
          setValues,
        }) => {
          return (
            <div className={styles.content}>
              <div className={styles.left}>
                <form className={styles.form}>
                  {dataLoading && isKurvPayEntity && (
                    <SkeletonLoader
                      variant="rectangular"
                      component="div"
                      height={"145px"}
                      width="100%"
                      style={{ borderRadius: "24px" }}
                    />
                  )}

                  {!dataLoading &&
                    isKurvPayEntity &&
                    emsOfficeOptions?.length > 1 && (
                      <AdditionalAppInfo
                        isOpen={openSections.additionalAppInfo}
                        onToggle={() =>
                          setOpenSections((prev) => ({
                            ...prev,
                            additionalAppInfo: !prev.additionalAppInfo,
                          }))
                        }
                        emsOfficeOptions={emsOfficeOptions}
                      />
                    )}

                  {dataLoading && (
                    <SkeletonLoader
                      variant="rectangular"
                      component="div"
                      height={isEMSUser ? "624px" : "442px"}
                      width="100%"
                      style={{ borderRadius: "24px" }}
                    />
                  )}

                  {!dataLoading && (
                    <ContactInformation
                      isOpen={openSections.contact}
                      onToggle={() =>
                        setOpenSections((prev) => ({
                          ...prev,
                          contact: !prev.contact,
                        }))
                      }
                    />
                  )}

                  <div className={styles.template_section}>
                    <div className={styles.text}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M8 1.5C6.71442 1.5 5.45772 1.88122 4.3888 2.59545C3.31988 3.30968 2.48676 4.32484 1.99479 5.51256C1.50282 6.70028 1.37409 8.00721 1.6249 9.26809C1.8757 10.529 2.49477 11.6872 3.40381 12.5962C4.31285 13.5052 5.47104 14.1243 6.73192 14.3751C7.99279 14.6259 9.29973 14.4972 10.4874 14.0052C11.6752 13.5132 12.6903 12.6801 13.4046 11.6112C14.1188 10.5423 14.5 9.28558 14.5 8C14.4982 6.27665 13.8128 4.62441 12.5942 3.40582C11.3756 2.18722 9.72335 1.50182 8 1.5ZM7.75 4.5C7.89834 4.5 8.04334 4.54399 8.16668 4.6264C8.29002 4.70881 8.38615 4.82594 8.44291 4.96299C8.49968 5.10003 8.51453 5.25083 8.48559 5.39632C8.45665 5.5418 8.38522 5.67544 8.28033 5.78033C8.17544 5.88522 8.04181 5.95665 7.89632 5.98559C7.75083 6.01453 7.60003 5.99968 7.46299 5.94291C7.32595 5.88614 7.20881 5.79001 7.1264 5.66668C7.04399 5.54334 7 5.39834 7 5.25C7 5.05109 7.07902 4.86032 7.21967 4.71967C7.36032 4.57902 7.55109 4.5 7.75 4.5ZM8.5 11.5C8.23479 11.5 7.98043 11.3946 7.7929 11.2071C7.60536 11.0196 7.5 10.7652 7.5 10.5V8C7.36739 8 7.24022 7.94732 7.14645 7.85355C7.05268 7.75979 7 7.63261 7 7.5C7 7.36739 7.05268 7.24021 7.14645 7.14645C7.24022 7.05268 7.36739 7 7.5 7C7.76522 7 8.01957 7.10536 8.20711 7.29289C8.39465 7.48043 8.5 7.73478 8.5 8V10.5C8.63261 10.5 8.75979 10.5527 8.85356 10.6464C8.94732 10.7402 9 10.8674 9 11C9 11.1326 8.94732 11.2598 8.85356 11.3536C8.75979 11.4473 8.63261 11.5 8.5 11.5Z"
                          fill="#626168"
                        />
                      </svg>
                      <span>
                        Speed up the app by using a Pricing + Equipment template
                      </span>
                    </div>
                    <div className={styles.right}>
                      {templateListLoading ? (
                        <SkeletonLoader
                          variant="rectangular"
                          height="32px"
                          width="200px"
                          component="div"
                          style={{ borderRadius: "8px" }}
                        />
                      ) : (
                        <Select
                          label=""
                          placeholder="Apply template"
                          id="apply_template"
                          name="apply_template"
                          options={templateOptions}
                          search
                          customClass={styles.templateSelect}
                          value={selectedTemplate}
                          onChange={(value) => {
                            setSelectedTemplate(value);
                            if (value) {
                              applyTemplate(value, setValues, values);
                            } else {
                              setFieldValue("templateApplied", false);
                              setFieldValue(
                                "nonEditableTemplateApplied",
                                false,
                              );
                              setFieldValue("template_id", null);
                              setValues(initialValuesBackup);
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {values.nonEditableTemplateApplied && (
                    <Alert fullWidth type="warning">
                      A non-editable template has been applied
                    </Alert>
                  )}

                  {dataLoading && (
                    <SkeletonLoader
                      variant="rectangular"
                      component="div"
                      height="73px"
                      width="100%"
                      style={{ borderRadius: "24px" }}
                    />
                  )}

                  {dataLoading && (
                    <SkeletonLoader
                      variant="rectangular"
                      component="div"
                      height="73px"
                      width="100%"
                      style={{ borderRadius: "24px" }}
                    />
                  )}

                  {dataLoading && (
                    <SkeletonLoader
                      variant="rectangular"
                      component="div"
                      height="73px"
                      width="100%"
                      style={{ borderRadius: "24px" }}
                    />
                  )}

                  {dataLoading && (
                    <SkeletonLoader
                      variant="rectangular"
                      component="div"
                      height="73px"
                      width="100%"
                      style={{ borderRadius: "24px" }}
                    />
                  )}

                  {!dataLoading && (
                    <>
                      <Processors
                        isOpen={openSections.processors}
                        onToggle={() =>
                          setOpenSections((prev) => ({
                            ...prev,
                            processors: !prev.processors,
                          }))
                        }
                      />

                      <Pricing
                        isOpen={openSections.pricing}
                        onToggle={() =>
                          setOpenSections((prev) => ({
                            ...prev,
                            pricing: !prev.pricing,
                          }))
                        }
                      />

                      <Equipments
                        isOpen={openSections.equipments}
                        onToggle={() =>
                          setOpenSections((prev) => ({
                            ...prev,
                            equipments: !prev.equipments,
                          }))
                        }
                      />

                      <Details
                        isOpen={openSections.details}
                        onToggle={() =>
                          setOpenSections((prev) => ({
                            ...prev,
                            details: !prev.details,
                          }))
                        }
                        orgEnablesPersonalGuarantor={
                          !!user?.organization?.enable_personal_guarantor_toggle
                        }
                        canManagePersonalGuarantor={
                          !!user?.organization?.enable_personal_guarantor_toggle
                        }
                      />
                    </>
                  )}

                  <Footer
                    setSubmissionType={setSubmissionType}
                    setOpenSections={setOpenSections}
                    dataLoading={dataLoading}
                  />
                </form>
              </div>
              <div className={styles.right}>
                <Cost />

                <div className={styles.mobileFooter}>
                  <Footer
                    setSubmissionType={setSubmissionType}
                    setOpenSections={setOpenSections}
                    dataLoading={dataLoading}
                  />
                </div>
              </div>

              <SendLinkMerchantModal
                onClose={() => setSendLinkModal(false)}
                isOpen={sendLinkModal}
                onContinue={() => {
                  history.push(`/${continueURL}`);
                }}
                applicationURL={`${window.location.origin}/${continueURL}`}
              />
            </div>
          );
        }}
      </Formik>
      {/* Toaster global component */}
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            borderRadius: "8px",
            background: "rgba(40, 39, 45, 0.85)",
            color: "#fff",
            padding: "16px 24px",
          },
          duration: 5000,
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                <button
                  type="button"
                  className={styles.toast_dismissal_button}
                  onClick={() => toast.dismiss(t.id)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M12.8535 12.1462C12.9 12.1927 12.9368 12.2478 12.962 12.3085C12.9871 12.3692 13.0001 12.4343 13.0001 12.5C13.0001 12.5657 12.9871 12.6307 12.962 12.6914C12.9368 12.7521 12.9 12.8073 12.8535 12.8537C12.8071 12.9002 12.7519 12.937 12.6912 12.9622C12.6305 12.9873 12.5655 13.0003 12.4998 13.0003C12.4341 13.0003 12.369 12.9873 12.3083 12.9622C12.2476 12.937 12.1925 12.9002 12.146 12.8537L7.99979 8.70685L3.85354 12.8537C3.75972 12.9475 3.63247 13.0003 3.49979 13.0003C3.36711 13.0003 3.23986 12.9475 3.14604 12.8537C3.05222 12.7599 2.99951 12.6327 2.99951 12.5C2.99951 12.3673 3.05222 12.24 3.14604 12.1462L7.29291 7.99997L3.14604 3.85372C3.05222 3.7599 2.99951 3.63265 2.99951 3.49997C2.99951 3.36729 3.05222 3.24004 3.14604 3.14622C3.23986 3.0524 3.36711 2.99969 3.49979 2.99969C3.63247 2.99969 3.75972 3.0524 3.85354 3.14622L7.99979 7.2931L12.146 3.14622C12.2399 3.0524 12.3671 2.99969 12.4998 2.99969C12.6325 2.99969 12.7597 3.0524 12.8535 3.14622C12.9474 3.24004 13.0001 3.36729 13.0001 3.49997C13.0001 3.63265 12.9474 3.7599 12.8535 3.85372L8.70666 7.99997L12.8535 12.1462Z"
                      fill="white"
                    />
                  </svg>
                </button>
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
    </div>
  );
};

export default withRouter(CreateAppPage);
